import { useRef, useState } from "react";

import { supabase } from "./supabaseclient";
import { btn, C, fill, FONT, Header, label, sel, usePublicLang } from "./publicShared";

// Public "Join VedSanga as a founding astrologer" page + application form.
// Opened with /?page=astrologer. Saves straight to Supabase
// (table astrologer_applications — see supabase_astrologer_applications.sql):
// Row Level Security allows INSERT only, so the public cannot read anyone's
// application and nothing depends on the backend being up. No ID/PAN here —
// those are collected on the onboarding call.

const LANG_OPTS = [["te", "తెలుగు"], ["hi", "हिन्दी"], ["en", "English"], ["ta", "தமிழ்"], ["kn", "ಕನ್ನಡ"], ["mr", "मराठी"], ["other", "Other"]];
const SYSTEMS = ["Vedic (Parashari)", "KP", "Nadi", "Prashna", "Lal Kitab", "Numerology", "Vastu", "Palmistry"];

const T = {
  en: {
    title: "Join VedSanga as a founding astrologer",
    sub: "VedSanga is a Vedic astrology and Bhagavad Gita app for families, in Telugu, Hindi and English. We are inviting a few experienced, honest astrologers to guide our users.",
    apply: "Apply now — 5 minutes",
    whyTitle: "What you get",
    why: [
      "You keep 70% of every consultation during launch",
      "You set your own fee",
      "Weekly payment to your UPI account",
      "Your own profile link — clients you bring get you 80%",
      "“Founding astrologer” badge and first place in listings",
      "No pressure to sell remedies or gemstones",
    ],
    howTitle: "How it works",
    how: ["Apply on this page", "We call you for 10 minutes", "A short test reading on two charts", "Your profile goes live in the app"],
    rulesTitle: "Our promise to users — please read",
    rules: [
      "No fear: never frighten a person to sell a remedy",
      "No guaranteed predictions",
      "No medical, legal or investment advice — suggest a doctor, lawyer or adviser",
      "Keep consultations on VedSanga; don't move clients to personal numbers",
      "Extra care with marriage and relationship questions",
    ],
    formTitle: "Your application",
    photo: "Your photo *", photoBtn: "Choose photo", photoChange: "Change photo",
    photoHelp: "A clear, recent photo of your face — no filters, logos or deity images. It is shown on your profile only after we approve you.",
    photoBad: "Please choose a photo (JPG or PNG).",
    name: "Full name *", phone: "WhatsApp number *", email: "Email", city: "City", state: "State",
    langs: "Languages you consult in *", systems: "Systems you practise",
    exp: "Years of experience *", training: "Your training or guru (where and from whom you learnt)",
    fee: "Fee you would like for a 15-minute chat (₹)", avail: "When are you usually available? (days and hours)",
    links: "Instagram, YouTube or website (if any)",
    q: "A 29-year-old woman asks: “Why is my marriage getting delayed? Will it ever happen?” How would you answer her? *",
    about: "Anything else about you",
    agree: "I have read and agree to follow the promise to users above *",
    consent: "We will contact you on WhatsApp or phone about this application only. We never share your details.",
    submit: "Submit application", sending: "Sending…",
    missing: "Please fill the fields marked * and agree to the promise.",
    err: "Could not send. Please check your internet and try again.",
    doneTitle: "Thank you, {n} 🙏",
    doneText: "We have received your application. We will message you on WhatsApp within 3 days.",
  },
  hi: {
    title: "VedSanga के संस्थापक ज्योतिषी बनें",
    sub: "VedSanga परिवारों के लिए हिंदी, तेलुगु और अंग्रेज़ी में वैदिक ज्योतिष और भगवद गीता ऐप है। हम कुछ अनुभवी और ईमानदार ज्योतिषियों को अपने उपयोगकर्ताओं के मार्गदर्शन के लिए आमंत्रित कर रहे हैं।",
    apply: "अभी आवेदन करें — 5 मिनट",
    whyTitle: "आपको क्या मिलेगा",
    why: [
      "शुरुआती समय में हर परामर्श का 70% आपका",
      "फ़ीस आप खुद तय करें",
      "हर हफ़्ते आपके UPI खाते में भुगतान",
      "आपका अपना प्रोफ़ाइल लिंक — अपने लाए ग्राहकों पर 80%",
      "“संस्थापक ज्योतिषी” बैज और सूची में सबसे ऊपर स्थान",
      "उपाय या रत्न बेचने का कोई दबाव नहीं",
    ],
    howTitle: "प्रक्रिया",
    how: ["इस पेज पर आवेदन करें", "हम आपको 10 मिनट के लिए फ़ोन करेंगे", "दो कुंडलियों पर छोटी परीक्षा-रीडिंग", "ऐप में आपकी प्रोफ़ाइल लाइव"],
    rulesTitle: "उपयोगकर्ताओं से हमारा वादा — कृपया पढ़ें",
    rules: [
      "डर नहीं: उपाय बेचने के लिए कभी किसी को न डराएँ",
      "कोई पक्की भविष्यवाणी की गारंटी नहीं",
      "चिकित्सा, कानूनी या निवेश सलाह नहीं — डॉक्टर, वकील या सलाहकार का सुझाव दें",
      "परामर्श VedSanga पर ही रखें; ग्राहकों को निजी नंबर पर न ले जाएँ",
      "विवाह और रिश्तों के सवालों में विशेष संवेदनशीलता",
    ],
    formTitle: "आपका आवेदन",
    photo: "आपकी फ़ोटो *", photoBtn: "फ़ोटो चुनें", photoChange: "फ़ोटो बदलें",
    photoHelp: "आपके चेहरे की साफ़, हाल की फ़ोटो — बिना फ़िल्टर, लोगो या देवता की तस्वीर। स्वीकृति के बाद ही आपकी प्रोफ़ाइल पर दिखेगी।",
    photoBad: "कृपया एक फ़ोटो चुनें (JPG या PNG)।",
    name: "पूरा नाम *", phone: "WhatsApp नंबर *", email: "ईमेल", city: "शहर", state: "राज्य",
    langs: "किन भाषाओं में परामर्श देते हैं *", systems: "आप कौन-सी पद्धति अपनाते हैं",
    exp: "अनुभव (वर्ष) *", training: "आपकी शिक्षा या गुरु (कहाँ और किससे सीखा)",
    fee: "15 मिनट की चैट के लिए आपकी फ़ीस (₹)", avail: "आप आम तौर पर कब उपलब्ध रहते हैं? (दिन और समय)",
    links: "Instagram, YouTube या वेबसाइट (यदि हो)",
    q: "29 साल की एक महिला पूछती हैं: “मेरी शादी में देरी क्यों हो रही है? क्या कभी होगी?” आप उन्हें क्या जवाब देंगे? *",
    about: "अपने बारे में और कुछ",
    agree: "मैंने ऊपर लिखा वादा पढ़ लिया है और उसका पालन करूँगा/करूँगी *",
    consent: "हम इस आवेदन के बारे में ही आपसे WhatsApp या फ़ोन पर संपर्क करेंगे। आपकी जानकारी कभी साझा नहीं करते।",
    submit: "आवेदन भेजें", sending: "भेजा जा रहा है…",
    missing: "कृपया * वाले सभी खाने भरें और वादे से सहमति दें।",
    err: "भेज नहीं सके। इंटरनेट जाँचकर फिर से कोशिश करें।",
    doneTitle: "धन्यवाद, {n} 🙏",
    doneText: "आपका आवेदन मिल गया है। हम 3 दिनों में WhatsApp पर संदेश भेजेंगे।",
  },
  te: {
    title: "VedSanga వ్యవస్థాపక జ్యోతిష్కులుగా చేరండి",
    sub: "VedSanga కుటుంబాల కోసం తెలుగు, హిందీ, ఇంగ్లీష్‌లో వైదిక జ్యోతిష్యం, భగవద్గీత యాప్. మా వినియోగదారులకు మార్గదర్శనం చేయడానికి అనుభవం, నిజాయితీ ఉన్న కొంతమంది జ్యోతిష్కులను ఆహ్వానిస్తున్నాం.",
    apply: "ఇప్పుడే దరఖాస్తు చేయండి — 5 నిమిషాలు",
    whyTitle: "మీకు లభించేది",
    why: [
      "ప్రారంభ సమయంలో ప్రతి సంప్రదింపులో 70% మీదే",
      "మీ ఫీజు మీరే నిర్ణయించండి",
      "ప్రతి వారం మీ UPI ఖాతాకు చెల్లింపు",
      "మీ సొంత ప్రొఫైల్ లింక్ — మీరు తెచ్చిన క్లయింట్లపై 80%",
      "“వ్యవస్థాపక జ్యోతిష్కులు” బ్యాడ్జ్, జాబితాలో ముందు స్థానం",
      "పరిహారాలు లేదా రత్నాలు అమ్మాలనే ఒత్తిడి లేదు",
    ],
    howTitle: "ఎలా జరుగుతుంది",
    how: ["ఈ పేజీలో దరఖాస్తు చేయండి", "మేము 10 నిమిషాలు ఫోన్ చేస్తాం", "రెండు జాతకాలపై చిన్న పరీక్ష రీడింగ్", "యాప్‌లో మీ ప్రొఫైల్ ప్రారంభం"],
    rulesTitle: "వినియోగదారులకు మా హామీ — దయచేసి చదవండి",
    rules: [
      "భయం వద్దు: పరిహారం అమ్మడానికి ఎవరినీ భయపెట్టవద్దు",
      "ఖచ్చితమైన జోస్యాల హామీ వద్దు",
      "వైద్య, న్యాయ, పెట్టుబడి సలహాలు వద్దు — డాక్టర్, లాయర్ లేదా సలహాదారుని సూచించండి",
      "సంప్రదింపులు VedSanga లోనే; క్లయింట్లను వ్యక్తిగత నంబర్‌కు తీసుకెళ్లవద్దు",
      "వివాహం, సంబంధాల ప్రశ్నల్లో ప్రత్యేక జాగ్రత్త",
    ],
    formTitle: "మీ దరఖాస్తు",
    photo: "మీ ఫోటో *", photoBtn: "ఫోటో ఎంచుకోండి", photoChange: "ఫోటో మార్చండి",
    photoHelp: "మీ ముఖం స్పష్టంగా కనిపించే ఇటీవలి ఫోటో — ఫిల్టర్లు, లోగోలు, దేవతా చిత్రాలు వద్దు. మేము ఆమోదించిన తర్వాతే మీ ప్రొఫైల్‌లో కనిపిస్తుంది.",
    photoBad: "దయచేసి ఒక ఫోటో ఎంచుకోండి (JPG లేదా PNG).",
    name: "పూర్తి పేరు *", phone: "WhatsApp నంబర్ *", email: "ఈమెయిల్", city: "నగరం", state: "రాష్ట్రం",
    langs: "ఏ భాషల్లో సంప్రదిస్తారు *", systems: "మీరు అనుసరించే పద్ధతులు",
    exp: "అనుభవం (సంవత్సరాలు) *", training: "మీ శిక్షణ లేదా గురువు (ఎక్కడ, ఎవరి దగ్గర నేర్చుకున్నారు)",
    fee: "15 నిమిషాల చాట్‌కు మీరు కోరే ఫీజు (₹)", avail: "సాధారణంగా ఎప్పుడు అందుబాటులో ఉంటారు? (రోజులు, సమయం)",
    links: "Instagram, YouTube లేదా వెబ్‌సైట్ (ఉంటే)",
    q: "29 ఏళ్ల ఒక మహిళ అడుగుతున్నారు: “నా పెళ్లి ఎందుకు ఆలస్యం అవుతోంది? అసలు జరుగుతుందా?” మీరు ఆమెకు ఏమని సమాధానం చెబుతారు? *",
    about: "మీ గురించి ఇంకేమైనా",
    agree: "పైన ఉన్న హామీని చదివాను, పాటిస్తాను *",
    consent: "ఈ దరఖాస్తు గురించి మాత్రమే WhatsApp లేదా ఫోన్‌లో సంప్రదిస్తాం. మీ వివరాలు ఎవరికీ ఇవ్వం.",
    submit: "దరఖాస్తు పంపండి", sending: "పంపుతున్నాం…",
    missing: "దయచేసి * ఉన్న అన్ని వివరాలు నింపి, హామీకి అంగీకరించండి.",
    err: "పంపలేకపోయాం. ఇంటర్నెట్ చూసి మళ్లీ ప్రయత్నించండి.",
    doneTitle: "ధన్యవాదాలు, {n} 🙏",
    doneText: "మీ దరఖాస్తు అందింది. 3 రోజుల్లో WhatsApp లో సందేశం పంపుతాం.",
  },
};

const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, display: "grid", gap: 12 };
const input = { ...sel, width: "100%", boxSizing: "border-box", padding: "0 12px" };
const area = { ...input, minHeight: 110, padding: 12, lineHeight: 1.5, fontSize: 17, resize: "vertical" };

function Chip({ on, children, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      style={{ minHeight: 48, padding: "0 16px", borderRadius: 24, fontSize: 16, fontWeight: 600, cursor: "pointer",
        border: `1.5px solid ${on ? C.accent : C.border}`, background: on ? C.warm : C.card, color: C.text }}>
      {on ? "✓ " : ""}{children}
    </button>
  );
}

/** Shrink to ≤ 800 px and re-encode as JPEG. Re-encoding through a canvas
 *  also drops EXIF metadata such as the GPS location phones embed. */
function shrinkPhoto(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 800 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode"))), "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("load")); };
    img.src = url;
  });
}

function Field({ id, text, children }) {
  return <div><label htmlFor={id} style={label}>{text}</label>{children}</div>;
}

export default function AstrologerJoinPage() {
  const [lang, setLang] = usePublicLang(T);
  const t = T[lang] || T.en;             // page is in en/hi/te; other languages see English
  const formRef = useRef(null);
  const [f, setF] = useState({
    full_name: "", phone: "", email: "", city: "", state: "", experience_years: "", training: "",
    fee_15min: "", availability: "", links: "", approach_answer: "", about: "", website: "",
  });
  const [langs, setLangs] = useState([]);
  const [systems, setSystems] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [photo, setPhoto] = useState(null);        // { blob, preview }
  const [photoBad, setPhotoBad] = useState(false);
  const fileRef = useRef(null);

  async function pickPhoto(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      if (!file.type.startsWith("image/")) throw new Error("type");
      const blob = await shrinkPhoto(file);
      if (photo) URL.revokeObjectURL(photo.preview);
      setPhoto({ blob, preview: URL.createObjectURL(blob) });
      setPhotoBad(false);
    } catch {
      setPhotoBad(true);
    }
  }
  const [state, setState] = useState("idle");       // idle | sending | done | missing | error
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const ready = f.full_name.trim().length >= 2 && f.phone.replace(/\D/g, "").length >= 10 && langs.length > 0
    && f.experience_years !== "" && f.approach_answer.trim().length > 0 && agreed && Boolean(photo);

  async function submit(e) {
    e.preventDefault();
    if (f.website) return;                           // honeypot: bots fill hidden fields
    if (!ready) { setState("missing"); return; }
    setState("sending");
    // Photo first (private bucket, upload-only for the public), then the row.
    const hex = (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9]/g, "").slice(0, 32);
    const photo_path = `applications/${hex}.jpg`;
    const up = await supabase.storage.from("astrologer-photos")
      .upload(photo_path, photo.blob, { contentType: "image/jpeg", upsert: false });
    if (up.error) { setState("error"); return; }
    const clip = (s, n) => (s.trim() ? s.trim().slice(0, n) : null);
    const { error } = await supabase.from("astrologer_applications").insert({
      full_name: f.full_name.trim().slice(0, 100),
      phone: f.phone.trim().slice(0, 20),
      email: clip(f.email, 120), city: clip(f.city, 80), state: clip(f.state, 80),
      languages: langs, systems,
      experience_years: Math.max(0, Math.min(80, parseInt(f.experience_years, 10) || 0)),
      training: clip(f.training, 1000),
      fee_15min: f.fee_15min ? Math.max(0, Math.min(100000, parseInt(f.fee_15min, 10) || 0)) : null,
      availability: clip(f.availability, 300), links: clip(f.links, 500),
      approach_answer: clip(f.approach_answer, 2000), about: clip(f.about, 1500),
      agreed_rules: true,
      photo_path,
    });
    setState(error ? "error" : "done");
    if (!error) window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const Section = ({ title, items, numbered }) => (
    <div style={card}>
      <div style={{ fontSize: 20, fontWeight: 800 }}>{title}</div>
      {items.map((x, i) => (
        <div key={x} style={{ display: "flex", gap: 10, fontSize: 17, lineHeight: 1.5 }}>
          <span style={{ color: C.accentDark, fontWeight: 800, minWidth: 22 }}>{numbered ? `${i + 1}.` : "✓"}</span><span>{x}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT, color: C.text }}>
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "20px 16px 48px", display: "grid", gap: 18 }}>
        <Header lang={lang} setLang={setLang} />

        {state === "done" ? (
          <div style={{ ...card, background: C.warm, border: `2px solid ${C.gold}`, textAlign: "center", padding: 28 }}>
            <div style={{ fontSize: 26, fontWeight: 800 }}>{fill(t.doneTitle, { n: f.full_name.trim().split(" ")[0] })}</div>
            <p style={{ fontSize: 18, lineHeight: 1.6, margin: 0 }}>{t.doneText}</p>
          </div>
        ) : (
          <>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 7vw, 38px)", lineHeight: 1.15, margin: 0 }}>{t.title}</h1>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: C.dim, margin: 0 }}>{t.sub}</p>
            <button onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth" })} style={btn(C.accent)}>{t.apply}</button>

            <Section title={t.whyTitle} items={t.why} />
            <Section title={t.howTitle} items={t.how} numbered />
            <div style={{ ...card, border: `1.5px solid ${C.gold}`, background: C.warm }}>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{t.rulesTitle}</div>
              {t.rules.map((r) => <div key={r} style={{ fontSize: 17, lineHeight: 1.5 }}>• {r}</div>)}
            </div>

            <form ref={formRef} onSubmit={submit} style={{ ...card, gap: 18 }} noValidate>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{t.formTitle}</div>
              {/* honeypot — hidden from people, bots fill it */}
              <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")}
                style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }} />

              <div>
                <span style={label}>{t.photo}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ width: 110, height: 110, borderRadius: "50%", border: `2px solid ${photo ? C.gold : C.border}`,
                    background: C.warm, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
                    {photo ? <img src={photo.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontSize: 40 }} aria-hidden="true">🙏</span>}
                  </div>
                  <button type="button" onClick={() => fileRef.current?.click()}
                    style={{ ...btn(C.card, C.text), width: "auto", border: `1.5px solid ${C.accent}`, flex: "1 1 180px" }}>
                    📷 {photo ? t.photoChange : t.photoBtn}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} style={{ display: "none" }} />
                </div>
                <div style={{ fontSize: 15, color: C.dim, marginTop: 8, lineHeight: 1.5 }}>{t.photoHelp}</div>
                {photoBad && <div style={{ fontSize: 16, color: "#B42318", fontWeight: 600, marginTop: 6 }}>{t.photoBad}</div>}
              </div>

              <Field id="a-name" text={t.name}><input id="a-name" value={f.full_name} onChange={set("full_name")} maxLength={100} style={input} autoComplete="name" /></Field>
              <Field id="a-phone" text={t.phone}><input id="a-phone" value={f.phone} onChange={set("phone")} maxLength={20} inputMode="tel" style={input} autoComplete="tel" /></Field>
              <Field id="a-email" text={t.email}><input id="a-email" value={f.email} onChange={set("email")} maxLength={120} inputMode="email" style={input} autoComplete="email" /></Field>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}><Field id="a-city" text={t.city}><input id="a-city" value={f.city} onChange={set("city")} maxLength={80} style={input} /></Field></div>
                <div style={{ flex: 1 }}><Field id="a-state" text={t.state}><input id="a-state" value={f.state} onChange={set("state")} maxLength={80} style={input} /></Field></div>
              </div>

              <div>
                <span style={label}>{t.langs}</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {LANG_OPTS.map(([k, n]) => <Chip key={k} on={langs.includes(k)} onClick={() => toggle(langs, setLangs, k)}>{n}</Chip>)}
                </div>
              </div>
              <div>
                <span style={label}>{t.systems}</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {SYSTEMS.map((s) => <Chip key={s} on={systems.includes(s)} onClick={() => toggle(systems, setSystems, s)}>{s}</Chip>)}
                </div>
              </div>

              <Field id="a-exp" text={t.exp}><input id="a-exp" value={f.experience_years} onChange={set("experience_years")} inputMode="numeric" maxLength={2} style={{ ...input, maxWidth: 160 }} /></Field>
              <Field id="a-training" text={t.training}><textarea id="a-training" value={f.training} onChange={set("training")} maxLength={1000} style={area} /></Field>
              <Field id="a-fee" text={t.fee}><input id="a-fee" value={f.fee_15min} onChange={set("fee_15min")} inputMode="numeric" maxLength={6} style={{ ...input, maxWidth: 200 }} /></Field>
              <Field id="a-avail" text={t.avail}><input id="a-avail" value={f.availability} onChange={set("availability")} maxLength={300} style={input} /></Field>
              <Field id="a-links" text={t.links}><input id="a-links" value={f.links} onChange={set("links")} maxLength={500} style={input} /></Field>
              <Field id="a-q" text={t.q}><textarea id="a-q" value={f.approach_answer} onChange={set("approach_answer")} maxLength={2000} style={{ ...area, minHeight: 150 }} /></Field>
              <Field id="a-about" text={t.about}><textarea id="a-about" value={f.about} onChange={set("about")} maxLength={1500} style={area} /></Field>

              <label style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 17, lineHeight: 1.5, cursor: "pointer" }}>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ width: 26, height: 26, marginTop: 2, flex: "0 0 auto" }} />
                <span>{t.agree}</span>
              </label>
              <div style={{ fontSize: 15, color: C.dim, lineHeight: 1.5 }}>{t.consent}</div>

              {state === "missing" && <div style={{ fontSize: 16, color: "#B42318", fontWeight: 600 }}>{t.missing}</div>}
              {state === "error" && <div style={{ fontSize: 16, color: "#B42318", fontWeight: 600 }}>{t.err}</div>}
              <button type="submit" disabled={state === "sending"} style={{ ...btn(C.accent), opacity: state === "sending" ? 0.6 : 1 }}>
                {state === "sending" ? t.sending : t.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
